import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSupabaseQuery } from '../../hooks/useSupabase';
import {
  Building2,
  Users,
  BarChart3,
  ArrowRight,
} from 'lucide-react';

export function CompanyAdminDashboard() {
  const { profile } = useAuth();

  // Get company info
  const { data: company } = useSupabaseQuery('companies', {
    filter: (q: any) => q.eq('id', profile?.companyId),
    single: true,
    enabled: !!profile?.companyId,
  });

  // Get departments
  const { data: departmentsData } = useSupabaseQuery('departments', {
    filter: (q: any) => q.eq('company_id', profile?.companyId),
    enabled: !!profile?.companyId,
  });
  const departments = departmentsData || [];

  // Get users in company
  const { data: usersData } = useSupabaseQuery('profiles', {
    filter: (q: any) => q.eq('company_id', profile?.companyId),
    enabled: !!profile?.companyId,
  });
  const users = usersData || [];

  const activeUsers = users.filter((u: any) => u.is_active !== false);

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">
          Velkommen til bedriftsadministrasjon
        </h1>
        <p className="text-blue-100">
          Her kan du administrere din bedrift, avdelinger og brukere.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Bedrift</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {company?.name || 'Laster...'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Avdelinger</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {departments.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Aktive brukere</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {activeUsers.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Link
          to="/company-admin/departments"
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-6 hover:shadow-md transition-shadow group"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Administrer avdelinger
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Opprett, rediger eller slett avdelinger i din bedrift.
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
          </div>
        </Link>

        <Link
          to="/company-admin/users"
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-6 hover:shadow-md transition-shadow group"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Administrer brukere
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Se og administrer alle brukere i din bedrift.
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
          </div>
        </Link>
      </div>

      {/* Recent Departments */}
      {departments.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Avdelinger
            </h2>
            <Link
              to="/company-admin/departments"
              className="text-blue-600 dark:text-blue-400 text-sm hover:underline"
            >
              Se alle
            </Link>
          </div>
          <div className="space-y-3">
            {departments.slice(0, 5).map((dept: any) => (
              <div
                key={dept.id}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{dept.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {dept.allocated_seats || 0} plasser
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
